import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BatchPaymentProvider } from "@icoach/batchPayment/component/BatchPaymentContext";
import { OverviewListTable, PageLayout, TabsCardLayout, EditableInfo } from "@icoach/components";
import { Link, useParams } from "react-router-dom";
import { ACCOUNT_BOOK_BULK_DEBIT_URL } from "@icoach/router/routerPath";
import useOrderBatchPaymentApi from "@apis/useOrderBatchPaymentApi";
import useMessageDialog from "@util/hook/useMessageDialog";
import useExportActionDialog from "@util/hook/useExportActionDialog";
import AccountBookDialogBox from "@icoach/accountBook/dialog/AccountBookDialogBox";
import { formatCurrencyFn, isEqual, isFunction } from "@util/utils";
import { DateTimeStandard, Alert, Card, Typography } from "@curves/ui-base";
import { parseDate } from "@util/moment";
import { Grid } from "@mui/material";
import useTabState from "@util/hook/useTabState";
import {
	BILLING_TARGET_FOR_CREDIT_CARD,
	TAB_FAILURE_STATE,
	TAB_IDLE_STATE,
	TAB_SUCCESS_STATE
} from "@icoach/batchPayment/staticData";
import clsx from "clsx";
import { DebitListForACH, DebitListForCreditCard } from "@icoach/batchPayment/component";
import { getBillingCreditCardRowsLabel } from "@icoach/batchPayment/component/BillingCreditCardRowsLabelTemplate";
import { getBillingACHRowsLabel } from "@icoach/batchPayment/component/BillingACHRowsLabelTemplate";

const BatchPaymentBillingDetailPage = () => {
	const { billingID } = useParams();
	const { getInitBatchPaymentBillingApi } = useOrderBatchPaymentApi();
	const [sourceData, setSourceData] = useState({});
	const { billing, records, failureRecords } = sourceData?.billingData || {};
	const {
		canCancel,
		canAchAction,
		billingStatus,
		reBillingQuantity,
		failureQuantity,
		successQuantity,
		billingTarget
	} = billing || {};

	const { deleteOrderBatchPaymentBillingApi, postOrderBatchPaymentAchExportApi } = useOrderBatchPaymentApi();
	const setMessageDialog = useMessageDialog();
	const { showExportConfirmDialog } = useExportActionDialog({ checkExportPermission: false });

	const { activeTab, handleTabChange } = useTabState(TAB_IDLE_STATE);

	const dialogRef = useRef(null);

	const doGetInitBatchPaymentBillingApi = useCallback(async () => {
			let resp = await getInitBatchPaymentBillingApi(billingID);
			if (resp) setSourceData(resp);
		},
		// eslint-disable-next-line
		[billingID]);

	const actions = useMemo(() => {
			let actionArray = [];

			if (canAchAction) {
				actionArray = [
					...actionArray,
					{
						label: "下載扣款名單",
						onClick: async () => {
							await showExportConfirmDialog({}, (params) => {
								postOrderBatchPaymentAchExportApi(billingID, params);
							});
						}
					},
					{
						label: "回填扣款紀錄",
						onClick: () => {
							dialogRef.current && isFunction(dialogRef.current.openImportAchDialog) && dialogRef.current.openImportAchDialog({ billingID });
						}
					}
				];
			}
			if (canCancel) {
				actionArray = [
					...actionArray,
					{
						label: "取消批次扣款",
						color: "error",
						onClick: () => {
							setMessageDialog({
								open: true,
								title: "確認取消批次扣款",
								msg: "取消後將停止交易作業且無法恢復，請問確定要取消嗎?",
								onAsyncConfirm: async (_, onClose) => {
									let res = await deleteOrderBatchPaymentBillingApi(billingID);
									if (res) {
										// todo refresh
										onClose();
									}
								}
							});
						}
					}
				];
			}

			actionArray = [
				...actionArray,
				{
					label: "返回列表",
					to: ACCOUNT_BOOK_BULK_DEBIT_URL,
					variant: "contained",
					component: Link
				}
			];
			return actionArray;
		},
		// eslint-disable-next-line
		[canAchAction, canCancel, billingID]);

	const listTabData = useMemo(() => {
			let tabs = [
				{
					label: "扣款名單",
					value: TAB_IDLE_STATE
				}
			];


			// 已有成功或失敗比數
			if (isEqual(billingStatus, 5)) {
				tabs = [
					{
						label: (
							<Typography
								className={clsx("tabs-label", { "tab-selected": isEqual(activeTab, TAB_FAILURE_STATE) })}>
								失敗名單
								<span className={"tabs-label-count"}>{failureQuantity}</span>
							</Typography>
						),
						value: TAB_FAILURE_STATE
					},
					{
						label: (
							<Typography
								className={clsx("tabs-label", { "tab-selected": isEqual(activeTab, TAB_SUCCESS_STATE) })}>
								成功名單
								<span className={"tabs-label-count"}>{successQuantity}</span>
							</Typography>
						),
						value: TAB_SUCCESS_STATE
					}
				];
			}

			return tabs;
		},
		// eslint-disable-next-line
		[billingStatus, failureQuantity, successQuantity, activeTab]);

	const headerRow = useMemo(
		() => {
			return isEqual(billingTarget, BILLING_TARGET_FOR_CREDIT_CARD) ? getBillingCreditCardRowsLabel() : getBillingACHRowsLabel();
		},
		// eslint-disable-next-line
		[billingTarget]
	);


	useEffect(() => {
			if (billingID) doGetInitBatchPaymentBillingApi();
		},
		// eslint-disable-next-line
		[billingID]);

	useEffect(() => {
			if (isEqual(billingStatus, 5)) {
				handleTabChange(null, TAB_FAILURE_STATE);
			}

		},
		// eslint-disable-next-line
		[billingStatus]);


	return (
		<BatchPaymentProvider>
			<PageLayout>
				<PageLayout.Header title="批次扣款" actions={actions} />
				<Card className={"mb-3 p-3"}>
					<Grid spacing={2} container>
						<Grid xs={3} item>
							<EditableInfo label={"帳單狀態"}>
								{billing?.billingStatusText}
							</EditableInfo>
						</Grid>
						<Grid xs={3} item>
							<EditableInfo label={"扣款方式"}>
								{billing?.billingTargetText}
							</EditableInfo>
						</Grid>
						<Grid xs={3} item>
							<EditableInfo label={"送件日期"}>
								{billing?.sendDate ? parseDate(billing?.sendDate, DateTimeStandard.DisplayDateBySlash) : "無相關資料"}
							</EditableInfo>
						</Grid>
						<Grid xs={3} item>
							<EditableInfo label={"擔當"}>
								{billing?.employeeDisplayName}
							</EditableInfo>
						</Grid>
						<Grid xs={3} item>
							<EditableInfo label={"總筆數"}>
								{billing?.totalQuantity}
							</EditableInfo>
						</Grid>
						<Grid xs={3} item>
							<EditableInfo label={"扣款金額"}>
								{formatCurrencyFn(billing?.paymentPrice, { symbol: "$" })}
							</EditableInfo>
						</Grid>
						<Grid xs={3} item>
							<EditableInfo label={"退款金額"}>
								{formatCurrencyFn(billing?.returnPrice, { symbol: "$" })}
							</EditableInfo>
						</Grid>
						<Grid xs={3} item>
							<EditableInfo label={"請款成功金額"}>
								{formatCurrencyFn(billing?.successPaymentPrice, { symbol: "$" })}
							</EditableInfo>
						</Grid>
					</Grid>
				</Card>
				<TabsCardLayout activeTab={activeTab} onTabChange={handleTabChange} tabs={listTabData}
								tabsContent={{ className: "p-3" }}>
					{
						Boolean(reBillingQuantity) && (
							<Alert className="alert-justify-content-start mb-2" variant="filled" severity="info"
								   color="info">
								{`已有 ${reBillingQuantity} 筆名單重新發送批次扣款。`}
							</Alert>
						)
					}
					{
						isEqual(activeTab, TAB_FAILURE_STATE) ? (
							isEqual(billingTarget, BILLING_TARGET_FOR_CREDIT_CARD) ? (
								<DebitListForCreditCard
									list={failureRecords}
									searchParams={{
										type: billing?.billingCategory,
										date: parseDate(billing?.sendDate, "YYYY-MM")
									}}
									headerRow={headerRow}
									dialogRef={dialogRef}
									refresh={doGetInitBatchPaymentBillingApi}
								/>
							) : (
								<DebitListForACH
									list={failureRecords}
									searchParams={{
										type: billing?.billingCategory,
										date: parseDate(billing?.sendDate, "YYYY-MM")
									}}
									headerRow={headerRow}
									dialogRef={dialogRef}
									refresh={doGetInitBatchPaymentBillingApi}
								/>
							)
						) : (
							<>
								<OverviewListTable
									className={"debit-list-table"}
									list={records}
									headerRow={headerRow}
									tableRowBodyProps={{
										dialogRef,
										refresh: doGetInitBatchPaymentBillingApi
										// isRevealed
									}}
									isGray
								/>
							</>
						)
					}
				</TabsCardLayout>
				<AccountBookDialogBox ref={dialogRef} />
			</PageLayout>
		</BatchPaymentProvider>
	);
};

export default BatchPaymentBillingDetailPage;